import { LAZO_TENANT_EXTERNAL_ID, useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { GridViewOutlined } from "@mui/icons-material";
import { Box, ListItemButton, ListItemIcon, ListItemText, styled } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import CompanyAvatar from "../common/CompanyAvatar";
import TenantSelect from "./TenantSelect";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  width: "100%",
  paddingTop: "0.5rem",
});

const CompanyAvatarContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
});

const MyCompaniesNavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "$active" && prop !== "$open",
})<{ $active: boolean; $open: boolean }>(({ theme, $active, $open }) => ({
  minHeight: 40,
  padding: $open ? "0.5rem 0" : "0.5rem 1rem",
  marginTop: $open ? 0 : theme.spacing(0.5),
  marginBottom: 0,
  justifyContent: $open ? "initial" : "center",
  color: $active ? Colors.LAZO_PURPLE2 : Colors.WHITE,
  borderRadius: $open ? "0.5rem" : 0,
  display: "flex",
  alignItems: "center",
}));

const NotificationDot = styled(Box)({
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: Colors.LAZO_PURPLE2,
});

interface DrawerHeaderSectionProps {
  open: boolean;
}

export default function DrawerHeaderSection({ open }: DrawerHeaderSectionProps) {
  const { t } = useTranslation("navigation");
  const { currentCompany, workspaceState } = useTenantMemberships();
  const location = useLocation();

  const companyName = currentCompany?.companyName || currentCompany?.name || "";
  const logoUrl = currentCompany?.logoUrl;

  const totalCompanies = useMemo(() => {
    return (workspaceState?.workspaces ?? [])
      .flatMap((w) => w?.memberships ?? [])
      .filter((m) => m?.tenant?.externalId && m.tenant.externalId !== LAZO_TENANT_EXTERNAL_ID).length;
  }, [workspaceState?.workspaces]);

  const showMyCompanies = totalCompanies > 1;
  const isActive = location.pathname === LazoPaths.MY_COMPANIES;

  const myCompaniesItem = showMyCompanies ? (
    <MyCompaniesNavItem component={NavLink} to={LazoPaths.MY_COMPANIES} $active={isActive} $open={open}>
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: open ? 3 : "auto",
          justifyContent: "center",
          color: isActive ? Colors.LAZO_PURPLE2 : Colors.WHITE,
          "& svg": { width: 18, height: 18 },
        }}
      >
        <GridViewOutlined />
      </ListItemIcon>
      {open && <ListItemText primary={t("drawer.lazoOne.myCompanies")} />}
      {open && isActive && <NotificationDot />}
    </MyCompaniesNavItem>
  ) : null;

  if (!open) {
    return myCompaniesItem;
  }

  return (
    <MainContainer>
      {myCompaniesItem}
      <CompanyAvatarContainer>
        <CompanyAvatar companyName={companyName} logoUrl={logoUrl} />
      </CompanyAvatarContainer>
      <TenantSelect />
    </MainContainer>
  );
}
